import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { Download, Loader2, TrendingUp } from 'lucide-react';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { fmtEur } from './gedUtils';
import { PRODUITS_FILTRE } from '../comptabilite/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/paiements-cb/admin`;
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const lab = (k) => `${MOIS[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;

const Kpi = ({ label, value, tone, testId }) => (
  <div className={`rounded-xl border p-3 ${tone}`} data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
    <p className="text-lg font-bold tabular-nums">{value}</p>
  </div>
);

const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

const ExportComptable = ({ getAuthHeader, superAdmin }) => {
  const [mois, setMois] = useState(prevMonth());
  const [produit, setProduit] = useState('');
  const [expert, setExpert] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/expert-comptable`, { headers: getAuthHeader() }).then((r) => setExpert(r.data.email || '')).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const exporter = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/export-comptable.xlsx`, { headers: getAuthHeader(), params: { mois, produit }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `export_comptable_${produit ? `${produit}_` : ''}${mois}.xlsx` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error('Export impossible'); } finally { setBusy(false); }
  };
  const saveExpert = async () => {
    try { await axios.put(`${API}/settings/expert-comptable`, { email: expert }, { headers: getAuthHeader() }); toast.success('Adresse de l\'expert-comptable enregistrée'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="export-comptable">
      <span className="text-sm font-medium text-slate-700">Export comptable</span>
      <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-8 w-40 text-sm" data-testid="export-comptable-mois" />
      <select value={produit} onChange={(e) => setProduit(e.target.value)} data-testid="export-comptable-produit"
        className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-700">
        {PRODUITS_FILTRE.map(([k, l]) => <option key={k || 'tous'} value={k}>{l}</option>)}
      </select>
      <button type="button" onClick={exporter} disabled={busy || !mois} data-testid="export-comptable-btn"
        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Exporter Excel
      </button>
      <span className="text-xs text-slate-500 ml-2">Envoi automatique le 1er du mois aux admins{expert ? ` et à ${expert}` : ''}</span>
      {superAdmin && (
        <span className="flex items-center gap-1 ml-auto">
          <Input type="email" value={expert} onChange={(e) => setExpert(e.target.value)} placeholder="E-mail de l'expert-comptable" className="h-8 w-60 text-sm" data-testid="expert-comptable-input" />
          <button type="button" onClick={saveExpert} className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100" data-testid="expert-comptable-save-btn">Enregistrer</button>
        </span>
      )}
    </div>
  );
};

export const EncaissementsDashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const [data, setData] = useState(null);
  const [mois, setMois] = useState(12);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/encaissements`, { headers: getAuthHeader(), params: { mois } })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [mois]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = data?.totaux || {};
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="encaissements-dashboard">
      <div className="flex flex-wrap items-center gap-2">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <h3 className="font-bold text-slate-900">Encaissements</h3>
        <div className="ml-auto flex gap-1">
          {[6, 12, 24].map((m) => (
            <button key={m} type="button" onClick={() => setMois(m)} data-testid={`encaissements-mois-${m}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${mois === m ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{m} mois</button>
          ))}
        </div>
      </div>
      <ExportComptable getAuthHeader={getAuthHeader} superAdmin={user?.role === 'super_admin'} />
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.error ? <p className="text-sm text-red-600">Chargement impossible</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <Kpi label="Encaissé CB" value={fmtEur(t.cb)} tone="border-emerald-200 bg-emerald-50 text-emerald-900" testId="kpi-cb" />
            <Kpi label="Encaissé SEPA" value={fmtEur(t.sepa)} tone="border-sky-200 bg-sky-50 text-sky-900" testId="kpi-sepa" />
            <Kpi label="Prélèvements réussis" value={`${t.prelevements_reussis} · ${fmtEur(t.montant_preleve)}`} tone="border-violet-200 bg-violet-50 text-violet-900" testId="kpi-preleves" />
            <Kpi label="Prélèvements refusés" value={t.prelevements_refuses} tone="border-orange-200 bg-orange-50 text-orange-900" testId="kpi-refuses" />
            <Kpi label="Impayés récupérés" value={fmtEur(t.impayes_recuperes)} tone="border-amber-200 bg-amber-50 text-amber-900" testId="kpi-recuperes" />
          </div>
          <div className="h-64" data-testid="encaissements-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.serie.map((x) => ({ ...x, label: lab(x.mois) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="eur" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="nb" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v, n) => (n.startsWith('Prélèvements') ? v : fmtEur(v))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="eur" dataKey="cb" name="CB" stackId="e" fill="#10b981" />
                <Bar yAxisId="eur" dataKey="sepa" name="SEPA" stackId="e" fill="#0ea5e9" />
                <Line yAxisId="eur" dataKey="impayes_recuperes" name="Impayés récupérés" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line yAxisId="nb" dataKey="prelevements_reussis" name="Prélèvements réussis" stroke="#7c3aed" strokeWidth={2} />
                <Line yAxisId="nb" dataKey="prelevements_refuses" name="Prélèvements refusés" stroke="#ef4444" strokeDasharray="4 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
};
